import { useMemo, useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { COUNTRY_CODES } from "@/lib/countryCodes";

interface CountryCodeSelectProps {
  value?: string | null;
  onChange: (value: string) => void;
  className?: string;
  disabled?: boolean;
}

export function CountryCodeSelect({ value, onChange, className, disabled }: CountryCodeSelectProps) {
  const [open, setOpen] = useState(false);

  const options = useMemo(() => {
    if (value && !COUNTRY_CODES.some((c) => c.value === value)) {
      return [{ value, iso: "", name: "Código salvo", flag: "🏳️", label: value }, ...COUNTRY_CODES];
    }
    return COUNTRY_CODES;
  }, [value]);

  const selected = options.find((c) => c.value === value);

  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          disabled={disabled}
          aria-label="Código do país"
          className={cn("w-28 justify-between rounded-full px-3 font-normal", className)}
        >
          <span className="truncate">
            {selected ? `${selected.flag} ${selected.value}` : "País"}
          </span>
          <ChevronsUpDown className="ml-1 h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start">
        <Command>
          <CommandInput placeholder="Buscar país ou código..." />
          <CommandList className="max-h-72 overflow-y-auto overscroll-contain" onWheel={(e) => e.stopPropagation()}>
            <CommandEmpty>Nenhum país encontrado.</CommandEmpty>
            <CommandGroup>
              {options.map((c) => (
                <CommandItem
                  key={`${c.value}-${c.iso}`}
                  value={`${c.name} ${c.iso} ${c.value} ${c.value.replace("+", "")}`}
                  onSelect={() => {
                    onChange(c.value);
                    setOpen(false);
                  }}
                >
                  <span className="mr-2">{c.flag}</span>
                  <span className="flex-1 truncate">{c.name}</span>
                  <span className="ml-2 text-muted-foreground">{c.value}</span>
                  <Check className={cn("ml-2 h-4 w-4", value === c.value ? "opacity-100" : "opacity-0")} />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
